import {
  CurveCalculator,
  Raydium,
  TxVersion,
} from "@raydium-io/raydium-sdk-v2";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountInstruction,
  getAssociatedTokenAddress,
  NATIVE_MINT,
  TOKEN_PROGRAM_ID,
} from "@solana/spl-token";
import {
  Connection,
  LAMPORTS_PER_SOL,
  PublicKey,
  SystemProgram,
  SYSVAR_RENT_PUBKEY,
  Transaction,
  TransactionInstruction,
} from "@solana/web3.js";
import BN from "bn.js";
import { Buffer } from "buffer";
import { confirmTransaction } from "./TransactionUtils";

// Environment Configuration
export const rpcUrl = import.meta.env.PROD
  ? import.meta.env.VITE_REACT_APP_NEXT_PUBLIC_RPC_URL_PRODUCTION
  : import.meta.env.VITE_REACT_APP_NEXT_PUBLIC_RPC_URL_DEVELOPMENT;

export const solanaNetwork = import.meta.env.PROD ? "mainnet-beta" : "devnet";

// Bonding Curve Configuration
const PROGRAM_ID = new PublicKey(
  "CEXYxkb7eSc311CW1eQRof5qwHZhEeitgu4K4KreC7uP",
);
const CURVE_CONFIG_KEY = new PublicKey(
  "HwDwC6tT6JRWkzyueYDNMmCENPWDkqQHznwqh9VoVAPk",
);
const BUY_SIGHASH = Buffer.from([102, 6, 61, 18, 1, 218, 235, 234]);
const SELL_SIGHASH = Buffer.from([51, 230, 133, 164, 1, 127, 131, 173]);

// Token constants
// TODO move to config or load from token metadata
const TOKEN_DECIMALS = 6;
const TOKEN_CONVERSION_FACTOR = 10 ** TOKEN_DECIMALS;

/**
 * Initializes the raydium sdk
 *
 * @param {*} connection
 * @param {*} signer
 * @returns
 */
export const initRaydiumSdk = async (
  connection: any,
  owner: any,
  isDevnet: boolean,
) => {
  const raydium = await Raydium.load({
    connection,
    owner,
    cluster: isDevnet ? "devnet" : "mainnet",
  });
  return raydium;
};

/**
 * Executes a buy order on the bonding curve or raydium
 *
 * @param {*} wallet
 * @param {*} amount
 * @param {*} tokenStage
 * @param {*} contractAddress
 * @param {*} bondingCurveData
 * @returns
 */
export const buyTokens = async (
  wallet: any,
  amount: number,
  tokenStage: "graduated" | "minted",
  contractAddress: string,
  bondingCurveData: any,
  raydiumPoolAddress: string,
) => {
  try {
    // Parse data
    const amountInLamports = BigInt(amount * LAMPORTS_PER_SOL);
    const userKey = new PublicKey(wallet.address);
    const tokenMint = new PublicKey(contractAddress);

    // Setup connection
    const connection = new Connection(rpcUrl);

    // Get and validate balance
    const balance = await connection.getBalance(userKey);
    if (balance < amountInLamports) {
      console.warn("Insufficient balance", balance);
      return false;
    }

    // Instruction to buy tokens
    let ataIx = null;
    const buyIxs = [];

    // Create buy instruction on bonding curve
    if (tokenStage === "minted") {
      // Get or create user's token account
      const userTokenAccount = await getAssociatedTokenAddress(
        tokenMint,
        userKey,
      );
      const userTokenAccountInfo =
        await connection.getAccountInfo(userTokenAccount);
      if (!userTokenAccountInfo) {
        ataIx = createAssociatedTokenAccountInstruction(
          userKey,
          userTokenAccount,
          userKey,
          tokenMint,
        );
      }

      // Parse bonding curve data
      const poolKey = new PublicKey(bondingCurveData.poolAddress);
      const poolTokenAccount = new PublicKey(bondingCurveData.poolTokenAccount);
      const poolSolVault = new PublicKey(bondingCurveData.poolSolVault);

      // Create IX
      buyIxs.push(
        new TransactionInstruction({
          programId: PROGRAM_ID,
          keys: [
            { pubkey: CURVE_CONFIG_KEY, isSigner: false, isWritable: true },
            { pubkey: poolKey, isSigner: false, isWritable: true },
            { pubkey: tokenMint, isSigner: false, isWritable: true },
            { pubkey: poolTokenAccount, isSigner: false, isWritable: true },
            { pubkey: poolSolVault, isSigner: false, isWritable: true },
            { pubkey: userTokenAccount, isSigner: false, isWritable: true },
            { pubkey: userKey, isSigner: true, isWritable: true },
            { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
            {
              pubkey: SystemProgram.programId,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            {
              pubkey: ASSOCIATED_TOKEN_PROGRAM_ID,
              isSigner: false,
              isWritable: false,
            },
          ],
          data: Buffer.concat([
            BUY_SIGHASH,
            Buffer.from(
              new Uint8Array(new BigUint64Array([amountInLamports]).buffer),
            ),
          ]),
        }),
      );

      // Create buy instruction on raydium
    } else if (tokenStage === "graduated") {
      // Init raydium sdk
      const raydium = await initRaydiumSdk(
        connection,
        userKey,
        solanaNetwork === "devnet",
      );

      // Get full pool data from Raydium
      const { poolInfo, poolKeys, rpcData } =
        await raydium.cpmm.getPoolInfoFromRpc(raydiumPoolAddress);

      // Setup swap
      // @ts-expect-error BigInt isn't allowed
      const swapAmount = new BN(amountInLamports);
      const baseReserve = rpcData.baseReserve;
      const quoteReserve = rpcData.quoteReserve;
      const inputMint = NATIVE_MINT.toBase58();
      const baseIn = inputMint === poolInfo.mintA.address;
      if (
        inputMint !== poolInfo.mintA.address &&
        inputMint !== poolInfo.mintB.address
      ) {
        throw new Error("input mint does not match pool");
      }

      const swapResult = CurveCalculator.swap(
        swapAmount,
        baseIn ? baseReserve : quoteReserve,
        baseIn ? quoteReserve : baseReserve,
        // @ts-expect-error configInfo is optional
        rpcData.configInfo?.tradeFeeRate,
      );
      const swap = await raydium.cpmm.swap({
        poolInfo,
        poolKeys,
        inputAmount: swapAmount,
        swapResult,
        slippage: 0.01, // TODO: Remove hard coding
        baseIn,
        txVersion: TxVersion.LEGACY,
        config: {
          associatedOnly: true,
        },
      });
      buyIxs.push(...swap.transaction.instructions);

      // Invalid token stage
    } else {
      console.warn("Invalid token stage", tokenStage);
    }

    // Return false if no instruction was created
    if (buyIxs.length === 0) {
      return false;
    }

    // Create transaction
    const tx = new Transaction();
    if (ataIx) {
      tx.add(ataIx);
    }
    tx.add(...buyIxs);

    // Send transaction
    const signature = await wallet.sendTransaction(tx, connection);
    console.log("Buy transaction sent:", signature);

    // Confirm transaction
    await confirmTransaction(signature);
    console.log("Buy transaction confirmed");

    return true;
  } catch (error) {
    console.error("Error buying tokens", error);
  }

  return false;
};

export const sellTokens = async (
  wallet: any,
  amount: number,
  tokenStage: "graduated" | "minted",
  contractAddress: string,
  bondingCurveData: any,
  raydiumPoolAddress: string,
) => {
  try {
    // Parse data
    const amountInLamports = BigInt(amount * TOKEN_CONVERSION_FACTOR);
    const userKey = new PublicKey(wallet.address);
    const tokenMint = new PublicKey(contractAddress);

    // Setup connection
    const connection = new Connection(rpcUrl);

    // Instruction to sell tokens
    const sellIxs = [];

    // Create sell instruction on bonding curve
    if (tokenStage === "minted") {
      // Get and validate user token account
      const userTokenAccount = await getAssociatedTokenAddress(
        tokenMint,
        userKey,
      );
      const userTokenAccountInfo =
        await connection.getTokenAccountBalance(userTokenAccount);
      if (!userTokenAccountInfo) {
        console.warn("User token account does not exist");
        return false;
      }
      const userTokenBalance = userTokenAccountInfo.value.amount;
      // @ts-expect-error userTokenBalance is string
      if (userTokenBalance < amount) {
        console.warn("Insufficient token balance", userTokenBalance);
        return false;
      }

      // Parse bonding curve data
      const poolKey = new PublicKey(bondingCurveData.poolAddress);
      const poolTokenAccount = new PublicKey(bondingCurveData.poolTokenAccount);
      const poolSolVault = new PublicKey(bondingCurveData.poolSolVault);
      const solVaultBump = bondingCurveData.solVaultBump;

      // Create IX
      sellIxs.push(
        new TransactionInstruction({
          programId: PROGRAM_ID,
          keys: [
            { pubkey: CURVE_CONFIG_KEY, isSigner: false, isWritable: true },
            { pubkey: poolKey, isSigner: false, isWritable: true },
            { pubkey: tokenMint, isSigner: false, isWritable: true },
            { pubkey: poolTokenAccount, isSigner: false, isWritable: true },
            { pubkey: poolSolVault, isSigner: false, isWritable: true },
            { pubkey: userTokenAccount, isSigner: false, isWritable: true },
            { pubkey: userKey, isSigner: true, isWritable: true },
            { pubkey: SYSVAR_RENT_PUBKEY, isSigner: false, isWritable: false },
            {
              pubkey: SystemProgram.programId,
              isSigner: false,
              isWritable: false,
            },
            { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
            {
              pubkey: ASSOCIATED_TOKEN_PROGRAM_ID,
              isSigner: false,
              isWritable: false,
            },
          ],
          data: Buffer.concat([
            SELL_SIGHASH,
            Buffer.from(
              new Uint8Array(new BigUint64Array([amountInLamports]).buffer),
            ),
            Buffer.from(new Uint8Array([solVaultBump])),
          ]),
        }),
      );

      // Create sell instruction on raydium
    } else if (tokenStage === "graduated") {
      // Init raydium sdk
      const raydium = await initRaydiumSdk(
        connection,
        userKey,
        solanaNetwork === "devnet",
      );

      // Get full pool data from Raydium
      const { poolInfo, poolKeys, rpcData } =
        await raydium.cpmm.getPoolInfoFromRpc(raydiumPoolAddress);

      // Setup swap
      // @ts-expect-error BigInt isn't allowed
      const swapAmount = new BN(amountInLamports);
      const baseReserve = rpcData.baseReserve;
      const quoteReserve = rpcData.quoteReserve;
      const inputMint = contractAddress;
      const baseIn = inputMint === poolInfo.mintA.address;

      if (
        inputMint !== poolInfo.mintA.address &&
        inputMint !== poolInfo.mintB.address
      ) {
        throw new Error("input mint does not match pool");
      }

      const swapResult = CurveCalculator.swap(
        swapAmount,
        baseIn ? baseReserve : quoteReserve,
        baseIn ? quoteReserve : baseReserve,
        // @ts-expect-error configInfo is optional
        rpcData.configInfo?.tradeFeeRate,
      );
      const swap = await raydium.cpmm.swap({
        poolInfo,
        poolKeys,
        inputAmount: swapAmount,
        swapResult,
        slippage: 0.01, // TODO: Remove hard coding
        baseIn,
        txVersion: TxVersion.LEGACY,
        config: {
          associatedOnly: true,
        },
      });
      sellIxs.push(...swap.transaction.instructions);

      // Invalid token stage
    } else {
      console.warn("Invalid token stage", tokenStage);
    }

    // Return false if no instruction was created
    if (sellIxs.length === 0) {
      return false;
    }

    // Create transaction
    const tx = new Transaction();
    tx.add(...sellIxs);

    // Send transaction
    const signature = await wallet.sendTransaction(tx, connection);
    console.log("Sell transaction sent:", signature);

    // Confirm transaction
    await confirmTransaction(signature);
    console.log("Sell transaction confirmed");

    return true;
  } catch (error) {
    console.error("Error buying tokens", error);
  }

  return false;
};
